import { AdaaHelper, isActivity, isService, isTpAggreementReadyToPublish } from "../../../../../../../core/utils";
import { SharedFields, textField } from "../../configs";
import {
  AccordionSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import {
  activitiesBudgetEnabledField,
  activityWorkloadsField,
  budgetField,
  cofogCategoryField,
  descriptionField,
  expensesField,
  highPriorityField,
  highPriorityJustificationField,
  hyperiondRefCodeField,
  initiativeManagerNameField,
  initiativesBudgetEnabledField,
  khadhamatiField,
  linkedNationalStrategiesField,
  linkedToNationalStrategiesField,
  monitoredField,
  objectiveIdField,
  otherCategoryField,
  progressField,
  progressManagerNameField,
  readyToPublishField,
  successFactorsField,
} from "./fields";

export const simpleScreenConfig = [
  objectiveIdField,
  ...SharedFields.name,
  ...descriptionField,
  {
    ...SharedFields.startDate,
    visibleIf: (data: { itemTypeId: number }) => !isTpAggreementReadyToPublish(data.itemTypeId),
  },
  {
    ...SharedFields.endDate,
    visibleIf: (data: { itemTypeId: number }) => !isTpAggreementReadyToPublish(data.itemTypeId),
  },
  readyToPublishField,
  {
    ...SharedFields.serviceField,
    visibleIf: (data: { typeId: number }) => isService(data.typeId),
  },
  khadhamatiField,
  cofogCategoryField,
  otherCategoryField,
  {
    ...SharedFields.initiativeNameField,
    visibleIf: (data: { itemTypeId: number }) => isActivity(data.itemTypeId),
  },
  SharedFields.projectTypeField,
  {
    ...SharedFields.orgUnitIdField,
    visibleIf: (data: { itemTypeId: number }) => !isTpAggreementReadyToPublish(data.itemTypeId),
  },
  {
    ...SharedFields.ownerField,
    visibleIf: (data: { itemTypeId: number }) => !isTpAggreementReadyToPublish(data.itemTypeId),
  },
  highPriorityField,
  linkedToNationalStrategiesField,
  highPriorityJustificationField,
  monitoredField,
  initiativeManagerNameField,
  progressManagerNameField,
  initiativesBudgetEnabledField,
  activitiesBudgetEnabledField,
  budgetField,
  expensesField,
  progressField,
  hyperiondRefCodeField,
  SharedFields.commentField,
  successFactorsField,
  activityWorkloadsField,
  linkedNationalStrategiesField,
  SharedFields.extendedFields,
  SharedFields.attachmentsField,
];

const colorField: ScreenSectionConfigType = {
  field: "iconColor",
  label: "government_scores.sectors.color",
  view: "normal",
};

const iconAttachField: ScreenSectionConfigType = {
  field: "iconAttach",
  label: "government_scores.sectors.icon",
  view: "normal",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format: ($o) => ($o as any).iconAttach?.filename,
  isClickable: true,
};

const pillarTypeField: ScreenSectionConfigType = {
  field: "pillarType",
  label: "government_scores.pillars.pillar_type",
  view: "normal",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "pillarTypeName"),
};

const weightField: ScreenSectionConfigType = {
  field: "weight",
  label: "nkpi.weight",
  view: "normal",
};

const executiveSummaryField: ScreenSectionConfigType = {
  field: "summary",
  label: "executive_dashboard.title",
  view: "normal",
};

const areaOfFocusName = () => {
  const fields = textField("areaName", { use: "both" }) as ScreenSectionConfigType[];

  return fields.map((field) => ({
    ...field,
    label: field.field === "areaNameAE" ? "common.form.label.nameAE" : "common.form.label.nameEN",
  }));
};
const areaOfFocusDsc = () => {
  const fields = textField("areaDsc", { use: "both" }) as ScreenSectionConfigType[];

  return fields.map((field) => ({
    ...field,
    label: field.field === "areaDscAE" ? "common.form.label.descriptionAE" : "common.form.label.descriptionEN",
  }));
};

const areaOfFocusSectionConfig: ScreenSectionConfigType[] = [...areaOfFocusName(), ...areaOfFocusDsc()];

const areaOfFocusField: AccordionSectionConfigType = {
  view: "accordion",
  field: "areasOfFocusList",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "areaName"),
  idKey: "id",
  config: areaOfFocusSectionConfig,
  label: "government_scores.pillars.area_of_focus.title",
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as Record<string, unknown>;
    const $o = $oldValue as Record<string, unknown>;
    const keys = ["id", "areaNameAE", "areaNameEN", "areaDscAE", "areaDscEN"];

    return keys.some((key) => $o[key] !== $n[key]);
  },
};

const involvedEntitiesField: ListSectionConfigType = {
  field: "involvedEntities",
  label: "cycle.connected_entities",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
};

const pillarField: ScreenSectionConfigType = {
  field: "subGovernmentDirectionId",
  label: "national_projects.transformational_targets.we_uae_pillar",
  view: "normal",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "subGovernmentDirectionName"),
};

const subPillarField: ScreenSectionConfigType = {
  field: "auxDirectionId",
  label: "national_projects.transformational_targets.we_uae_sub_pillar",
  view: "normal",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "auxDirectionName"),
};

export const govSectorsAuditScreenConfig = {
  type: "simple" as ScreenType,
  sections: [...SharedFields.name, ...descriptionField, colorField, iconAttachField, involvedEntitiesField],
};

export const entityPillarsAuditScreenConfig = {
  type: "simple" as ScreenType,
  sections: [SharedFields.entityIdField, pillarTypeField, weightField, executiveSummaryField, areaOfFocusField],
};

export const transformationalTargetsScreenConfig = {
  type: "metrics" as ScreenType,
  sections: [...SharedFields.name, ...SharedFields.description, pillarField, subPillarField],
};
